import React, { useState, useMemo, useCallback } from 'react';
import {
  cemeteryPlots as plotsApi,
  cemeteryBurials as burialsApi,
  cemeteryMaintenance as maintenanceApi
} from '../../api/endpoints';
import api, { getList } from '../../api/client';
import {
  useFetchList, useSearch, usePagination, useNotification, useDateRangeFilter, useOptionList,
  invalidateDirectory,
  useOpenRecordFromLink, RECORD_GONE
} from '../../hooks';
import { formatDate, formatCurrency, todayISO } from '../../utils/format';
import { printableRows, printReference } from '../../utils/printRows';
import useStyles from '../../theme/useStyles';
import { missingRequired, requiredError } from '../../utils/requiredFields';
import {
  PageHeader, SearchBar, DateRangeFilter, DataTable, Pagination, Notification, PromptDialog, ConfirmDialog, Modal, FormLayout, Input, Select, SearchableSelect, Badge, Button, KpiCard, MemberPicker, TablePanel, SubTabs, PrintButton
} from '../../components/ui';
import { hexToRgba } from '../../theme/tokens';
import type { CemeteryPlot, CemeteryBurial, CemeteryMaintenance } from '../../types';
import {
  occupancyByBlock, burialsByYear, burialsPerYear, yearsOfSpaceLeft,
  maintenanceByMonth, maintenanceBy, maintenanceInYear,
  orphanBurials, doubleBookedPlots, reservedAndWaiting, memorialsInMonth
} from './analytics';

const PLOT_STATUSES = ['Available', 'Reserved', 'Occupied', 'Under Maintenance'];

const EMPTY_PLOT = { plotNumber: '', section: '', status: 'Available', allottedTo: '', notes: '' };
const EMPTY_BURIAL = {
  date: '', dateOfDeath: '', deceasedName: '', memberId: '',
  assignedPlot: '', nextOfKin: '', notes: ''
};
const EMPTY_MAINTENANCE = { date: '', area: '', taskType: '', cost: '', notes: '' };

// "Block A - 101 (Allotted: John Doe)"
const plotLabel = (p) => {
  const base = `${p.section} - ${p.plotNumber}`;
  return p.allottedTo ? `${base} (Allotted: ${p.allottedTo})` : base;
};

const parsePlotLabel = (label) => {
  const clean = String(label).split(' (Allotted:')[0].trim();
  if (clean.includes(' - ')) {
    const [section, ...rest] = clean.split(' - ');
    return { section: section.trim(), plotNumber: rest.join(' - ').trim(), fullPlot: clean };
  }
  return { section: '', plotNumber: clean, fullPlot: clean };
};

/**
 * Plots and burials come from separate endpoints but share one list and one
 * voided log. `recordType` is the discriminant, so filtering on it narrows to
 * the right field set.
 */
// `status` is re-declared rather than intersected: a plot's status carries
// availability ('Available' | 'Occupied' | ...), which is wider than the
// ACTIVE/VOID RecordStatus on the base record.
// `lifecycle` is the ACTIVE/VOID soft-delete flag every record carries. For a
// plot it has to be held separately because `status` is overwritten with the
// plot's availability below — conflating the two is what used to make voiding a
// plot destroy whether it was Occupied.
type PlotRow = Omit<CemeteryPlot, 'status'> & { rawId?: number; status?: string; lifecycle?: string; recordType: 'Plot' };
type BurialRow = Omit<CemeteryBurial, 'status'> & { rawId?: number; status?: string; lifecycle?: string; recordType: 'Burial' };
type MaintenanceRow = Omit<CemeteryMaintenance, 'status'> & { rawId?: number; status?: string; lifecycle?: string; recordType: 'Maintenance' };
type CemeteryRow = PlotRow | BurialRow | MaintenanceRow;

/**
 * Plot creation and booking are separate steps over the same plot table: a plot
 * is cut first, then reserved for someone, then finally occupied by a burial.
 */
type CemeteryTab = 'summary' | 'dashboard' | 'setup' | 'allotment' | 'burials' | 'maintenance';

const CemeteryManagement = ({ isDarkMode }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;

  const [activeTab, setActiveTab] = useState<CemeteryTab>('dashboard');
  /** Dashboard shows either the pictorial plot map or the master list. */
  const [dashSubTab, setDashSubTab] = useState('grid');
  /** Plot creation: one at a time, or a numbered range. */
  const [setupMode, setSetupMode] = useState('single');
  const [bulkForm, setBulkForm] = useState({ prefix: '', startNum: '1', endNum: '10', section: '' });
  const [allotForm, setAllotForm] = useState({ targetPlot: '', allottedTo: '', bookingStatus: 'Reserved', notes: '' });
  const [viewMode, setViewMode] = useState('history');
  const [plotForm, setPlotForm] = useState(EMPTY_PLOT);
  const [burialForm, setBurialForm] = useState(EMPTY_BURIAL);
  const [maintForm, setMaintForm] = useState(EMPTY_MAINTENANCE);
  const [saving, setSaving] = useState(false);
  const [voiding, setVoiding] = useState(null);
  /** The plot whose card is open on the map. */
  const [plotDetail, setPlotDetail] = useState<PlotRow | null>(null);
  /** The record open for editing, or null when the form is filing a new one. */
  const [editing, setEditing] = useState<CemeteryRow | null>(null);
  /**
   * Marking a plot Occupied is the one change here that is hard to walk back —
   * it is how a grave leaves circulation — so every route to it asks first.
   */
  const [pendingOccupy, setPendingOccupy] = useState<{
    message: string; confirmLabel: string; run: () => void;
  } | null>(null);

  const { notification, notify, dismiss } = useNotification();
  const sections = useOptionList('church_cem_sections');
  const taskTypes = useOptionList('church_cem_task_types');

  // Plots and burials live on separate endpoints but share one voided log.
  const { data: combined, loading, refetch } = useFetchList<CemeteryRow>(async (p, o) => {
    const [dbPlots, dbBurials, dbMaintenance] = await Promise.all([
      api.get(plotsApi.url(), o).catch(() => []),
      api.get(burialsApi.url(), o).catch(() => []),
      api.get(maintenanceApi.url(), o).catch(() => [])
    ]);
    return [
      ...getList<CemeteryPlot>(dbPlots).map((x): PlotRow => ({
        ...x, rawId: x.id, status: x.plotStatus, lifecycle: x.status, recordType: 'Plot'
      })),
      ...getList<CemeteryBurial>(dbBurials).map((x): BurialRow => ({
        ...x, rawId: x.id, lifecycle: x.status, recordType: 'Burial'
      })),
      ...getList<CemeteryMaintenance>(dbMaintenance).map((x): MaintenanceRow => ({
        ...x, rawId: x.id, lifecycle: x.status, recordType: 'Maintenance'
      }))
    ];
  });

  const { plots, burials, maintenance, voidedPlots, voidedBurials, voidedMaintenance } = useMemo(() => {
    // Record ids read "CEM-9", "CEM-10": a plain string compare puts 10 before
    // 9, which is the "random" ordering reported (CHM-160 / CHM-169). Compared
    // by the trailing number instead. Plots list in creation order (1, 2, 3…),
    // while the burial and maintenance logs stay newest-first.
    const idNumber = (id?: string) => {
      const m = /(\d+)\s*$/.exec(String(id || ''));
      return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
    };
    const byRecordIdAsc = (a: CemeteryRow, b: CemeteryRow) =>
      idNumber(a.recordId) - idNumber(b.recordId)
      || String(a.recordId || '').localeCompare(String(b.recordId || ''));
    const byRecordId = (a: CemeteryRow, b: CemeteryRow) => byRecordIdAsc(b, a);
    const byDeleted = (a: CemeteryRow, b: CemeteryRow) =>
      new Date(b.deletedAt || 0).getTime() - new Date(a.deletedAt || 0).getTime();
    const all = combined;
    // `status` is checked too, for plots voided before the two were separated:
    // those rows had 'VOID' written into their availability as well.
    const isVoid = (x: CemeteryRow) => x.lifecycle === 'VOID' || x.status === 'VOID';
    return {
      plots: all.filter((x): x is PlotRow => x.recordType === 'Plot' && !isVoid(x)).sort(byRecordIdAsc),
      burials: all.filter((x): x is BurialRow => x.recordType === 'Burial' && !isVoid(x)).sort(byRecordId),
      maintenance: all.filter((x): x is MaintenanceRow => x.recordType === 'Maintenance' && !isVoid(x)).sort(byRecordId),
      voidedPlots: all.filter((x): x is PlotRow => x.recordType === 'Plot' && isVoid(x)).sort(byDeleted),
      voidedBurials: all.filter((x): x is BurialRow => x.recordType === 'Burial' && isVoid(x)).sort(byDeleted),
      voidedMaintenance: all.filter((x): x is MaintenanceRow => x.recordType === 'Maintenance' && isVoid(x)).sort(byDeleted)
    };
  }, [combined]);

  const isSummary = activeTab === 'summary';
  const isDashboard = activeTab === 'dashboard';
  const isSetup = activeTab === 'setup';
  const isAllotment = activeTab === 'allotment';
  const isBurials = activeTab === 'burials';
  const isMaintenance = activeTab === 'maintenance';
  const isDeletedView = viewMode === 'deleted';
  /** Plot creation and booking both read and write the plot table. */
  const isPlots = isSetup || isAllotment;

  const activeByTab = { plots, burials, maintenance };
  const voidedByTab = { plots: voidedPlots, burials: voidedBurials, maintenance: voidedMaintenance };
  // Two tabs — Plot Creation and Booking — both read the plot table, so the tab
  // key is not the dataset key. Indexing by the tab directly returned undefined
  // for those two, which is why their registry and voided log were always
  // empty however many plots existed.
  const datasetKey: 'plots' | 'burials' | 'maintenance' =
    isPlots ? 'plots' : activeTab === 'maintenance' ? 'maintenance' : 'burials';
  const wholeDataset: CemeteryRow[] = isDeletedView ? voidedByTab[datasetKey] : activeByTab[datasetKey];
  // Plot Creation lists every plot; Booking lists only the ones actually
  // booked. Both tabs read the same plot table, so a freshly created plot used
  // to show up as a booking record too (CHM-148). The booking form picks its
  // plot from its own list of open plots, so nothing is lost here.
  const isBooked = (r: CemeteryRow) =>
    Boolean(String((r as PlotRow).allottedTo || '').trim())
    || !['', 'Available'].includes(String((r as PlotRow).status || '').trim());
  const dataset: CemeteryRow[] = isAllotment && !isDeletedView
    ? wholeDataset.filter(isBooked)
    : wholeDataset;

  const getRowDate = useCallback((r) => r.date || r.dateAdded, []);
  const range = useDateRangeFilter(dataset, getRowDate);
  const { searchTerm, setSearchTerm, filtered, clear } = useSearch(
    range.filtered, ['recordId', 'plotNumber', 'allottedTo', 'deceasedName', 'section', 'area', 'taskType']
  );
  const page = usePagination(filtered);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setViewMode('history');
    setEditing(null);
    page.reset();
    clear();
    range.clear();
  };

  const switchView = (mode) => {
    // Stepping away from the form abandons the edit, so the next "+ Record New
    // Entry" starts blank instead of quietly overwriting the last row opened.
    if (mode !== 'add') {
      setEditing(null);
      setPlotForm(EMPTY_PLOT);
      setBurialForm(EMPTY_BURIAL);
      setMaintForm(EMPTY_MAINTENANCE);
    }
    setViewMode(mode);
    page.reset();
  };

  /**
   * A plot payload that keeps the two status fields apart: `plotStatus` is
   * availability, `status` is the soft-delete flag. Spreading a row sends the
   * availability into both, which is how the lifecycle flag ended up holding
   * values like "Available".
   */
  const plotPayload = (row: PlotRow, changes: Partial<CemeteryPlot> = {}) => ({
    ...(row as unknown as CemeteryPlot),
    id: row.rawId,
    plotStatus: row.status,
    status: (row.lifecycle || 'ACTIVE') as CemeteryPlot['status'],
    ...changes
  });

  /** Opens an existing record in its own form. */
  const startEdit = (row: CemeteryRow) => {
    setEditing(row);
    if (row.recordType === 'Plot') {
      setPlotForm({
        plotNumber: row.plotNumber || '', section: row.section || '',
        status: row.status || 'Available', allottedTo: row.allottedTo || '',
        notes: row.notes || ''
      });
    } else if (row.recordType === 'Burial') {
      setBurialForm({
        date: row.date || '', dateOfDeath: row.dateOfDeath || '',
        deceasedName: row.deceasedName || '', memberId: row.memberId || '',
        assignedPlot: row.section ? `${row.section} - ${row.plotNumber}` : (row.plotNumber || ''),
        nextOfKin: row.nextOfKin || '', notes: row.notes || ''
      });
    } else {
      setMaintForm({
        date: row.date || '', area: row.area || '', taskType: row.taskType || '',
        cost: row.cost === undefined || row.cost === null ? '' : String(row.cost),
        notes: row.notes || ''
      });
    }
    setViewMode('add');
  };

  // Dashboard needs its own pagination — the shared one follows the active tab.

  // "Open the record" from the Approvals inbox. A plot opens in its plot
  // details; a burial or maintenance entry has no view of its own, so its
  // form opens. A deleted one is shown, found by its number, in its tab's
  // Deleted list.
  useOpenRecordFromLink(({ id, ref }) => {
    const hits = combined.filter((r) => r.rawId === id);
    const row = (hits.length > 1 && ref ? hits.find((r) => r.recordId === ref) : null) || hits[0];
    if (!row) { notify(RECORD_GONE, 'error'); return; }
    const tab: CemeteryTab = row.recordType === 'Plot' ? 'setup' : row.recordType === 'Burial' ? 'burials' : 'maintenance';
    if (row.lifecycle === 'VOID' || row.status === 'VOID') {
      switchTab(tab);
      setViewMode('deleted');
      setSearchTerm(row.recordId || '');
    } else if (row.recordType === 'Plot') {
      switchTab('dashboard');
      setPlotDetail(row);
    } else {
      switchTab(tab);
      startEdit(row);
    }
  }, !loading);
  const plotPage = usePagination(plots);

  const blockNames = useMemo(
    () => [...new Set(plots.map((p) => p.section).filter(Boolean))].sort(),
    [plots]
  );

  const plotCounts = useMemo(
    () => plots.reduce<Record<string, number>>((acc, p) => {
      const key = String(p.status || 'Available');
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {}),
    [plots]
  );

  // Plots that can still receive a burial.
  const openPlots = useMemo(
    () => plots.filter((p) => p.status === 'Available' || p.status === 'Reserved'),
    [plots]
  );
  const openPlotLabels = useMemo(() => openPlots.map(plotLabel), [openPlots]);

  /**
   * Plots offered on the burial form. A burial being edited already occupies
   * its plot, so that plot is not in the open list — without adding it back the
   * form would open with its own plot missing and silently lose it on save.
   */
  const burialPlotOptions = useMemo(() => {
    const current = burialForm.assignedPlot;
    if (!current) return openPlotLabels;
    const same = (a: string, b: string) => {
      const x = parsePlotLabel(a); const y = parsePlotLabel(b);
      return x.section === y.section && x.plotNumber === y.plotNumber;
    };
    return openPlotLabels.some((l) => same(l, current)) ? openPlotLabels : [current, ...openPlotLabels];
  }, [openPlotLabels, burialForm.assignedPlot]);
  const reservedNames = useMemo(
    () => [...new Set(openPlots.filter((p) => p.allottedTo).map((p) => p.allottedTo))],
    [openPlots]
  );

  // Picking a name fills the plot, and picking a plot fills the name.
  const selectDeceased = (name: string) => {
    setBurialForm((prev) => {
      const match = openPlots.find((p) => p.allottedTo === name);
      return { ...prev, deceasedName: name, assignedPlot: match ? plotLabel(match) : prev.assignedPlot };
    });
  };

  const selectPlot = (label: string) => {
    const allotted = String(label).match(/\(Allotted: (.*?)\)/);
    setBurialForm((prev) => ({
      ...prev,
      assignedPlot: label,
      deceasedName: allotted?.[1] || prev.deceasedName
    }));
  };

  const handlePlotSubmit = async (e) => {
    e.preventDefault();

    const section = plotForm.section.trim();
    if (!section) { notify('Section/Block is required.', 'error'); return; }

    const editingPlot = editing?.recordType === 'Plot' ? (editing as PlotRow) : null;
    const duplicate = plots.some(
      (p) => p.rawId !== editingPlot?.rawId
        && p.plotNumber?.toLowerCase() === plotForm.plotNumber.trim().toLowerCase()
        && p.section?.toLowerCase() === section.toLowerCase()
    );
    if (duplicate) {
      notify(`Plot Number ${plotForm.plotNumber} already exists in ${section}!`, 'error');
      return;
    }

    // Setting the status by hand is the third way into Occupied, and the only
    // one with no burial behind it — so it is the one most worth pausing on.
    if (plotForm.status === 'Occupied' && editingPlot?.status !== 'Occupied') {
      setPendingOccupy({
        confirmLabel: 'Mark Occupied',
        message: `${section} - ${plotForm.plotNumber} will be marked Occupied by hand, with no `
          + 'burial record behind it. It will show under Data Check until a burial is entered '
          + 'for it, or the status is changed back.',
        run: () => savePlot({ section, editingPlot })
      });
      return;
    }
    savePlot({ section, editingPlot });
  };

  const savePlot = async ({ section, editingPlot }) => {
    setSaving(true);
    try {
      if (editingPlot) {
        await plotsApi.update(editingPlot.rawId, plotPayload(editingPlot, {
          plotNumber: plotForm.plotNumber.trim(),
          section,
          plotStatus: plotForm.status,
          allottedTo: plotForm.allottedTo,
          notes: plotForm.notes
        }));
      } else {
        // No record number — the server generates PLT-<id>.
        await plotsApi.create({
          ...plotForm,
          section,
          plotStatus: plotForm.status,
          dateAdded: todayISO(),
          status: 'ACTIVE' as const
        });
      }
      await refetch();
      notify(editingPlot ? 'Plot updated.' : 'Plot registered successfully.', 'success');
      setPlotForm(EMPTY_PLOT);
      setEditing(null);
      setViewMode('history');
    } catch (err) {
      notify(err.message || 'Could not save the plot.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBurialSubmit = async (e) => {
    e.preventDefault();

    const deceasedName = burialForm.deceasedName.trim();
    const assignedPlot = burialForm.assignedPlot.trim();
    const missing = missingRequired([[assignedPlot, 'Plot Number'], [deceasedName, 'Name of Deceased']]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }
    if (burialForm.dateOfDeath && burialForm.date && burialForm.dateOfDeath > burialForm.date) {
      notify('The date of death cannot be after the burial.', 'error');
      return;
    }

    const { section, plotNumber, fullPlot } = parsePlotLabel(assignedPlot);
    const editingBurial = editing?.recordType === 'Burial' ? (editing as BurialRow) : null;

    // Saving a burial takes the plot out of circulation and, when a member is
    // linked, marks them deceased. Worth one look before it happens.
    setPendingOccupy({
      confirmLabel: editingBurial ? 'Update Burial' : 'Confirm Burial',
      message: [
        `${section} - ${plotNumber} will be marked Occupied for ${deceasedName}.`,
        burialForm.memberId
          ? ` Member ${burialForm.memberId} will be recorded as deceased`
            + (burialForm.dateOfDeath ? ` on ${formatDate(burialForm.dateOfDeath)}.` : ', with no date of death on file.')
          : ''
      ].join(''),
      run: () => saveBurial({ deceasedName, section, plotNumber, fullPlot, editingBurial })
    });
  };

  const saveBurial = async ({ deceasedName, section, plotNumber, fullPlot, editingBurial }) => {
    setSaving(true);
    try {
      // One call either way. The server fills the plot, stamps the member and
      // numbers the record, all in one transaction — this used to be two calls
      // where a failure on the second was swallowed, leaving the plot free on
      // paper. An edit moves all three together in the same way.
      const body = {
        date: burialForm.date,
        dateOfDeath: burialForm.dateOfDeath,
        memberId: burialForm.memberId,
        nextOfKin: burialForm.nextOfKin,
        notes: burialForm.notes,
        deceasedName,
        plotNumber,
        section,
        fullPlot
      };

      if (editingBurial) {
        await burialsApi.update(editingBurial.rawId, {
          ...(editingBurial as unknown as CemeteryBurial),
          id: editingBurial.rawId,
          ...body,
          status: (editingBurial.lifecycle || 'ACTIVE') as CemeteryBurial['status']
        });
      } else {
        await burialsApi.create({ ...body, status: 'ACTIVE' as const });
      }

      invalidateDirectory();
      await refetch();
      notify(
        editingBurial ? 'Burial record updated.'
          : burialForm.memberId
            ? 'Burial recorded. The plot and the member record were updated.'
            : 'Burial recorded and the plot marked occupied.',
        'success'
      );
      setBurialForm(EMPTY_BURIAL);
      setEditing(null);
      setViewMode('history');
    } catch (err) {
      notify(err.message || 'Could not save the burial record.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Cuts a numbered range of plots in one go, skipping any that already exist. */
  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    const section = bulkForm.section.trim();
    const start = parseInt(bulkForm.startNum, 10);
    const end = parseInt(bulkForm.endNum, 10);

    if (!section) { notify('Section/Block is required.', 'error'); return; }
    if (Number.isNaN(start) || Number.isNaN(end)) { notify('Enter a valid number range.', 'error'); return; }
    if (end < start) { notify('The end number must not be lower than the start number.', 'error'); return; }
    if (end - start + 1 > 200) { notify('Generate at most 200 plots at a time.', 'error'); return; }

    sections.ensure(section);

    const existing = new Set(
      plots.filter((p) => p.section === section).map((p) => String(p.plotNumber).toLowerCase())
    );

    const wanted: string[] = [];
    for (let i = start; i <= end; i += 1) {
      const plotNumber = `${bulkForm.prefix}${i}`;
      if (!existing.has(plotNumber.toLowerCase())) wanted.push(plotNumber);
    }

    if (wanted.length === 0) {
      notify(`Every plot in that range already exists in ${section}.`, 'error');
      return;
    }

    setSaving(true);
    try {
      // Sequential rather than parallel to keep the generated numbers in the
      // order the plots were asked for; the server assigns each PLT-<id>.
      for (const plotNumber of wanted) {
        await plotsApi.create({
          plotNumber,
          section,
          plotStatus: 'Available',
          notes: '',
          dateAdded: todayISO(),
          status: 'ACTIVE' as const
        });
      }
      await refetch();
      const skipped = (end - start + 1) - wanted.length;
      notify(
        `${wanted.length} plot${wanted.length === 1 ? '' : 's'} created in ${section}.`
        + (skipped > 0 ? ` ${skipped} already existed and were skipped.` : ''),
        'success'
      );
      setBulkForm({ prefix: '', startNum: '1', endNum: '10', section });
      setViewMode('history');
    } catch (err) {
      notify(err.message || 'Could not generate the plots.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Books an available plot to a named person without burying anyone in it. */
  const handleAllotmentSubmit = async (e) => {
    e.preventDefault();
    const allottedTo = allotForm.allottedTo.trim();
    if (!allotForm.targetPlot) { notify('Select a plot to book.', 'error'); return; }
    if (!allottedTo) { notify('Enter who the plot is allotted to.', 'error'); return; }

    const { section, plotNumber } = parsePlotLabel(allotForm.targetPlot);
    const target = plots.find((p) => p.plotNumber === plotNumber && p.section === section);
    if (!target) { notify('That plot could not be found.', 'error'); return; }

    setSaving(true);
    try {
      await plotsApi.update(target.rawId, plotPayload(target, {
        allottedTo,
        plotStatus: allotForm.bookingStatus,
        notes: allotForm.notes || target.notes
      }));
      await refetch();
      notify(`${section} - ${plotNumber} booked for ${allottedTo}.`, 'success');
      setAllotForm({ targetPlot: '', allottedTo: '', bookingStatus: 'Reserved', notes: '' });
      setViewMode('history');
    } catch (err) {
      notify(err.message || 'Could not book the plot.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMaintenanceSubmit = async (e) => {
    e.preventDefault();

    const taskType = maintForm.taskType.trim();
    const area = maintForm.area.trim();
    const missing = missingRequired([[area, 'Area / Section'], [taskType, 'Task Type']]);
    if (missing.length) { notify(requiredError(missing), 'error'); return; }
    // Maintenance records work that has been carried out, so it cannot be
    // dated in the future. The input's max only guards the picker (CHM-145).
    if (maintForm.date && maintForm.date > todayISO()) {
      notify('The date of work cannot be in the future.', 'error'); return;
    }

    taskTypes.ensure(taskType);
    sections.ensure(area);

    const editingMaint = editing?.recordType === 'Maintenance' ? (editing as MaintenanceRow) : null;

    setSaving(true);
    try {
      const body = {
        date: maintForm.date,
        area,
        taskType,
        cost: maintForm.cost ? parseFloat(maintForm.cost) : 0,
        notes: maintForm.notes
      };
      if (editingMaint) {
        await maintenanceApi.update(editingMaint.rawId, {
          ...(editingMaint as unknown as CemeteryMaintenance),
          id: editingMaint.rawId,
          ...body,
          status: (editingMaint.lifecycle || 'ACTIVE') as CemeteryMaintenance['status']
        });
      } else {
        // No record number — the server generates CMT-<id>.
        await maintenanceApi.create({ ...body, status: 'ACTIVE' as const });
      }
      await refetch();
      notify(editingMaint ? 'Maintenance log updated.' : 'Maintenance log saved successfully.', 'success');
      setMaintForm(EMPTY_MAINTENANCE);
      setEditing(null);
      setViewMode('history');
    } catch (err) {
      notify(err.message || 'Could not save the maintenance log.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmVoid = async (reason) => {
    const item = voiding;
    setVoiding(null);
    // Guard: the dialog can only reach here with a row selected, but the
    // state is nulled before the await so TypeScript cannot prove it.
    if (!item) return;
    const resource =
      item.recordType === 'Plot' ? plotsApi
      : item.recordType === 'Burial' ? burialsApi
      : maintenanceApi;

    try {
      const payload = {
        ...item,
        id: item.rawId,
        status: 'VOID',
        deleteReason: reason,
        deletedAt: new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      };
      // `plotStatus` is deliberately left alone. It holds availability, and
      // `status` above already records the soft delete; writing 'VOID' into it
      // used to erase whether the plot was Occupied, with no way back.
      if (item.recordType === 'Plot') payload.plotStatus = item.status;

      await resource.update(item.rawId, payload);

      // Freeing the plot and reviving the member are the server's job now, in
      // the same transaction as the void.
      if (item.recordType === 'Burial') invalidateDirectory();

      await refetch();
      notify('Record voided. It is kept under Deleted History.', 'success');
    } catch (err) {
      notify(err.message || 'Could not delete the record.', 'error');
    }
  };

  /**
   * Plots and burials disagreeing with each other.
   *
   * These are the marks left by the old two-call save, which wrote the burial
   * and then updated the plot separately and swallowed any failure. Both
   * directions matter: a plot holding a burial but reading Available can be
   * sold to a second family, and one reading Occupied with nobody in it is a
   * grave withheld from use for no reason.
   */
  const mismatches = useMemo(() => {
    const key = (sec, num) => `${sec}||${num}`;
    const buried = new Map<string, BurialRow>();
    burials.forEach((b) => buried.set(key(b.section, b.plotNumber), b));

    const heldNotMarked = plots
      .filter((p) => p.status !== 'Occupied' && buried.has(key(p.section, p.plotNumber)))
      .map((p) => ({ plot: p, burial: buried.get(key(p.section, p.plotNumber)) as BurialRow }));

    const markedNotHeld = plots
      .filter((p) => p.status === 'Occupied' && !buried.has(key(p.section, p.plotNumber)));

    return { heldNotMarked, markedNotHeld };
  }, [plots, burials]);

  // Burials that name a plot which does not exist, and plots named by two
  // burials. The first is what a wrong block on the burial form leaves behind;
  // the grave is off the map and can be sold again.
  // The helpers work on the plain record shapes; the rows here are those plus
  // the screen's tags, so what comes back is narrowed to the row types again.
  const orphans = useMemo(
    () => orphanBurials(plots, burials) as Array<{ burial: BurialRow; candidates: PlotRow[] }>,
    [plots, burials]
  );
  const doubles = useMemo(
    () => doubleBookedPlots(burials) as Array<{ section?: string; plotNumber?: string; burials: BurialRow[] }>,
    [burials]
  );
  const mismatchCount = mismatches.heldNotMarked.length + mismatches.markedNotHeld.length + orphans.length + doubles.length;

  /** Moves a burial onto an existing plot; the server occupies it and frees the old one. */
  const repointBurial = async (burial: BurialRow, target: PlotRow) => {
    setSaving(true);
    try {
      await burialsApi.update(burial.rawId, {
        ...(burial as unknown as CemeteryBurial),
        id: burial.rawId,
        section: target.section,
        plotNumber: target.plotNumber,
        fullPlot: `${target.section} - ${target.plotNumber}`,
        status: (burial.lifecycle || 'ACTIVE') as CemeteryBurial['status']
      });
      await refetch();
      notify(`${burial.deceasedName || burial.recordId} moved to ${target.section} - ${target.plotNumber}.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not move the burial.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Cuts the plot the burial names, already Occupied, so the record stands. */
  const createPlotForBurial = async (burial: BurialRow) => {
    setSaving(true);
    try {
      await plotsApi.create({
        plotNumber: burial.plotNumber,
        section: burial.section,
        plotStatus: 'Occupied',
        allottedTo: burial.deceasedName || '',
        notes: `Created from burial ${burial.recordId || ''} by Data Check`,
        dateAdded: todayISO(),
        status: 'ACTIVE' as const
      });
      if (burial.section) sections.ensure(burial.section);
      await refetch();
      notify(`${burial.section} - ${burial.plotNumber} created and marked Occupied.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not create the plot.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Asks, then marks one mismatched plot Occupied. */
  const askFixHeldNotMarked = (entry) => setPendingOccupy({
    confirmLabel: 'Mark Occupied',
    message: `${entry.plot.section} - ${entry.plot.plotNumber} will be marked Occupied, `
      + `matching the burial of ${entry.burial.deceasedName || 'the person'} already on file `
      + `(${entry.burial.recordId || 'no record number'}). The burial record is not changed.`,
    run: async () => {
      try { await fixHeldNotMarked(entry); }
      catch (err) { notify(err.message || 'Could not fix the plot.', 'error'); }
    }
  });

  const askFixAllHeldNotMarked = () => setPendingOccupy({
    confirmLabel: `Mark ${mismatches.heldNotMarked.length} Occupied`,
    message: `${mismatches.heldNotMarked.length} plot(s) will be marked Occupied to match the `
      + 'burials already filed against them. No burial record is changed, and each plot can '
      + 'still be released again from this screen.',
    run: fixAllHeldNotMarked
  });

  /** Marks a plot Occupied to match the burial already filed against it. */
  const fixHeldNotMarked = async (entry, quiet = false) => {
    await plotsApi.update(entry.plot.rawId, plotPayload(entry.plot, {
      plotStatus: 'Occupied',
      allottedTo: entry.plot.allottedTo || entry.burial.deceasedName
    }));
    if (!quiet) {
      await refetch();
      notify(`${entry.plot.section} - ${entry.plot.plotNumber} marked Occupied.`, 'success');
    }
  };

  const fixAllHeldNotMarked = async () => {
    setSaving(true);
    try {
      for (const entry of mismatches.heldNotMarked) await fixHeldNotMarked(entry, true);
      await refetch();
      notify(`${mismatches.heldNotMarked.length} plot(s) marked Occupied.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not repair the plots.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /** Frees a plot that is marked Occupied with no burial behind it. */
  const fixMarkedNotHeld = async (p: PlotRow) => {
    try {
      await plotsApi.update(p.rawId, plotPayload(p, { plotStatus: 'Available' }));
      await refetch();
      notify(`${p.section} - ${p.plotNumber} released.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not release the plot.', 'error');
    }
  };

  /** The live burial filling a plot, if any. */
  const burialFor = useCallback(
    (p: PlotRow) => burials.find((b) => b.section === p.section && b.plotNumber === p.plotNumber),
    [burials]
  );

  // The map is where a clerk actually looks at the cemetery, so the three
  // things they want next -- book it, bury in it, read who is in it -- start
  // from the plot they just clicked rather than from a search box two tabs away.
  const bookPlot = (p: PlotRow) => {
    setPlotDetail(null);
    switchTab('allotment');
    setAllotForm({
      targetPlot: plotLabel(p),
      allottedTo: p.allottedTo || '',
      bookingStatus: 'Reserved',
      notes: ''
    });
    setViewMode('add');
  };

  const buryInPlot = (p: PlotRow) => {
    setPlotDetail(null);
    switchTab('burials');
    setBurialForm({ ...EMPTY_BURIAL, assignedPlot: plotLabel(p), deceasedName: p.allottedTo || '' });
    setViewMode('add');
  };

  const openBurialRegistry = () => {
    setPlotDetail(null);
    switchTab('burials');
  };

  /**
   * Puts a voided record back. Cemetery had no way to do this at all, so a
   * mistaken void was permanent.
   */
  const handleRestore = async (item) => {
    const resource =
      item.recordType === 'Plot' ? plotsApi
      : item.recordType === 'Burial' ? burialsApi
      : maintenanceApi;

    try {
      const payload: Record<string, unknown> = {
        ...item, id: item.rawId, status: 'ACTIVE', deleteReason: null, deletedAt: null
      };
      // Plots voided before availability and lifecycle were separated have
      // 'VOID' sitting in plotStatus. There is nothing to recover, so they come
      // back as Available; a burial still pointing at the plot re-occupies it.
      if (item.recordType === 'Plot') {
        payload.plotStatus = !item.status || item.status === 'VOID' ? 'Available' : item.status;
      }

      await resource.update(item.rawId, payload);
      if (item.recordType === 'Burial') invalidateDirectory();
      await refetch();
      notify(`${item.recordId || 'Record'} restored.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not restore the record.', 'error');
    }
  };

  const summary = useMemo(() => {
    const now = new Date();
    const perYear = burialsPerYear(burials, now);
    const years = burialsByYear(burials);
    const months = maintenanceByMonth(maintenance);
    return {
      blocks: occupancyByBlock(plots),
      years,
      peakYear: years.reduce((m, y) => Math.max(m, y.count), 0),
      perYear,
      yearsLeft: yearsOfSpaceLeft(plotCounts.Available || 0, perYear),
      thisYear: now.getFullYear(),
      maintThisYear: maintenanceInYear(maintenance, now.getFullYear()),
      months,
      peakMonth: months.reduce((m, x) => Math.max(m, x.total), 0),
      byTask: maintenanceBy(maintenance, 'taskType'),
      byArea: maintenanceBy(maintenance, 'area'),
      waiting: reservedAndWaiting(plots, now),
      memorials: memorialsInMonth(burials, now.getMonth() + 1, now),
      monthName: now.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
      noDeathDate: burials.filter((b) => !b.dateOfDeath).length
    };
  }, [plots, burials, maintenance, plotCounts]);

  const plotStatusTone = (status) =>
    status === 'Available' ? 'success' : status === 'Occupied' ? 'error' : 'neutral';

  const idColumn = { key: 'recordId', header: 'Record ID', nowrap: true, render: (r) => <strong>{r.recordId}</strong> };

  const plotColumns = [
    idColumn,
    { key: 'plotNumber', header: 'Plot No.', render: (r) => <strong>{r.plotNumber}</strong> },
    { key: 'section', header: 'Section/Block' },
    { key: 'allottedTo', header: 'Allotted To', render: (r) => <strong>{r.allottedTo || '-'}</strong> },
    { key: 'status', header: 'Status', render: (r) => <Badge tone={plotStatusTone(r.status)}>{r.status}</Badge> },
    { key: 'notes', header: 'Notes', wrap: true, render: (r) => r.notes || '-' }
  ];

  const burialColumns = [
    idColumn,
    { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
    { key: 'deceasedName', header: 'Deceased Name', render: (r) => <strong>{r.deceasedName}</strong> },
    {
      key: 'plot',
      header: 'Plot Allocated',
      render: (r) => <Badge tone="neutral">{r.section ? `${r.section} - ${r.plotNumber}` : r.plotNumber}</Badge>
    },
    { key: 'nextOfKin', header: 'Contact / Kin' },
    // Captured on the form but never shown in the table (CHM-027).
    { key: 'notes', header: 'Notes', wrap: true, render: (r) => r.notes || '-' }
  ];

  const maintenanceColumns = [
    idColumn,
    { key: 'date', header: 'Date', nowrap: true, render: (r) => formatDate(r.date) },
    { key: 'area', header: 'Area / Section', render: (r) => <strong>{r.area || '-'}</strong> },
    { key: 'taskType', header: 'Task', render: (r) => <Badge tone="neutral">{r.taskType}</Badge> },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      nowrap: true,
      render: (r) => <span style={{ color: t.expense, fontWeight: 'bold' }}>{formatCurrency(r.cost)}</span>
    },
    { key: 'notes', header: 'Notes', wrap: true, render: (r) => r.notes || '-' }
  ];

  const tailColumns = isDeletedView
    ? [
        { key: 'deletedAt', header: 'Deleted On', render: (r) => <span style={{ fontSize: '12px', color: t.textFaint }}>{r.deletedAt || '-'}</span> },
        { key: 'deleteReason', header: 'Reason', wrap: true, render: (r) => <span style={{ color: t.danger, fontStyle: 'italic' }}>{r.deleteReason || '-'}</span> },
        {
          key: 'actions',
          header: 'Actions',
          align: 'right',
          render: (r) => (
            <Button write variant="secondary" size="sm" onClick={() => handleRestore(r)}>♻️ Restore</Button>
          )
        }
      ]
    : [
        {
          key: 'actions',
          header: 'Actions',
          align: 'right',
          nowrap: true,
          render: (r) => (
            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
              {/* A burial is a record the family is given a copy of; a plot or
                  a maintenance line is not, so only burials offer it. */}
              {r.recordType === 'Burial' && (
                <PrintButton
                  module="Cemetery"
                  documentType="BURIAL_RECORD"
                  size="sm"
                  variant="ghost"
                  label="🖨️ Print"
                  title={`Burial-${r.recordId || r.rawId}`}
                  data={() => ({
                    ...r,
                    date: formatDate((r as BurialRow).date),
                    dateOfDeath: formatDate((r as BurialRow).dateOfDeath)
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
              )}
              <Button write variant="ghost" size="sm" onClick={() => startEdit(r)}>Edit</Button>
              <Button variant="danger" size="sm" style={styles.deleteBtn}
                onClick={() => {
                  // Deleting the plot under a burial would strand the grave off the map.
                  const held = r.recordType === 'Plot' ? burialFor(r as PlotRow) : undefined;
                  if (held) { notify(`${r.section} - ${r.plotNumber} holds the burial of ${held.deceasedName || held.recordId}. Delete or move that burial first.`, 'error'); return; }
                  setVoiding(r);
                }}>Delete</Button>
            </div>
          )
        }
      ];

  const bodyColumns = isPlots ? plotColumns : isBurials ? burialColumns : maintenanceColumns;
  const columns = [...bodyColumns, { key: 'createdBy', header: 'Created By', render: (r) => (r as { createdBy?: string }).createdBy || '-' }, ...tailColumns];

  return (
    <div style={{ ...styles.container, minHeight: '80vh' }}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        icon="🪦"
        title="Cemetery Management"
        activeTab={activeTab}
        onTabChange={switchTab}
        tabs={[
          { key: 'summary', label: 'Summary' },
          { key: 'dashboard', label: 'Plot map' },
          { key: 'setup', label: 'Plot creation' },
          { key: 'allotment', label: 'Booking' },
          { key: 'burials', label: 'Burials' },
          { key: 'maintenance', label: 'Maintenance' }
        ]}
      />

      {/* The map and summary are read-only, so the record/delete controls are hidden. */}
      {!isDashboard && !isSummary && (
        <SubTabs
          value={viewMode}
          onChange={switchView}
          tabs={[
            { key: 'history', label: 'Registry' },
            { key: 'deleted', label: 'Deleted', variant: 'danger' },
            { key: 'add', write: true, label: editing ? '✎ Editing entry…' : '+ Add new entry' }
          ]}
        />
      )}

      {isSummary && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: t.spacing.md, marginBottom: t.spacing.lg
          }}>
            <KpiCard label="Total Plots" value={String(plots.length)} color={t.primary} />
            <KpiCard label="Available" value={String(plotCounts.Available || 0)} color={t.income} />
            <KpiCard label="Reserved" value={String(plotCounts.Reserved || 0)} color={t.warning} />
            <KpiCard label="Occupied" value={String(plotCounts.Occupied || 0)} color={t.danger} />
            <KpiCard label="Under Maintenance" value={String(plotCounts['Under Maintenance'] || 0)} color={t.textFaint} />
            <KpiCard label="Burials / Year" value={summary.perYear === null ? '—' : String(summary.perYear)} color={t.info} />
            <KpiCard label="Years of Space Left" value={summary.yearsLeft === null ? '—' : `≈ ${summary.yearsLeft}`}
              color={summary.yearsLeft !== null && summary.yearsLeft < 5 ? t.danger : t.income} />
            <KpiCard label={`Maintenance ${summary.thisYear}`} value={formatCurrency(summary.maintThisYear)} color={t.expense} />
            <KpiCard label="Data Check" value={mismatchCount ? `${mismatchCount} issue${mismatchCount === 1 ? '' : 's'}` : 'Clean'}
              color={mismatchCount ? t.danger : t.income} />
          </div>
          {summary.perYear === null && (
            <div style={{ color: t.textFaint, fontSize: t.fontSize.sm, marginBottom: t.spacing.md }}>
              Burials per year and years of space need at least one complete past year of burial dates.
            </div>
          )}

          <TablePanel
            title="Occupancy by Block"
               isDarkMode={isDarkMode}
            minWidth="640px"
            columns={[
              { key: 'block', header: 'Block', render: (r) => <strong>{r.block}</strong> },
              { key: 'total', header: 'Plots', align: 'center' },
              { key: 'occupied', header: 'Occupied', align: 'center' },
              { key: 'reserved', header: 'Reserved', align: 'center' },
              { key: 'available', header: 'Available', align: 'center', render: (r) => <strong style={{ color: r.available ? t.income : t.danger }}>{r.available}</strong> },
              { key: 'maintenance', header: 'Maintenance', align: 'center' },
              {
                key: 'bar', header: 'Occupied %',
                render: (r) => (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ flex: 1, height: '10px', borderRadius: '4px', backgroundColor: t.bgSubtle, border: `1px solid ${t.border}` }}>
                      <div style={{ width: `${r.occupiedPct}%`, height: '100%', borderRadius: '4px', backgroundColor: t.danger, opacity: 0.85 }} />
                    </div>
                    <span style={{ minWidth: '44px', textAlign: 'right' }}>{r.occupiedPct}%</span>
                  </div>
                )
              }
            ]}
            rows={summary.blocks}
            rowKey="block"
            emptyMessage="No plots yet."
          />

          <TablePanel
            title="Burials by Year"
               isDarkMode={isDarkMode}
            minWidth="420px"
            columns={[
              { key: 'year', header: 'Year', nowrap: true, render: (r) => <strong>{r.year}</strong> },
              {
                key: 'bar', header: '',
                render: (r) => <div style={{ height: '10px', borderRadius: '4px', backgroundColor: t.info, opacity: 0.85, width: `${summary.peakYear ? Math.max(2, (r.count / summary.peakYear) * 100) : 0}%` }} />
              },
              { key: 'count', header: 'Burials', align: 'right' }
            ]}
            rows={summary.years}
            rowKey="year"
            emptyMessage="No burial dates recorded yet."
          />

          <TablePanel
            title="Maintenance Spend by Month"
               isDarkMode={isDarkMode}
            minWidth="420px"
            columns={[
              { key: 'month', header: 'Month', nowrap: true, render: (r) => <strong>{r.month}</strong> },
              {
                key: 'bar', header: '',
                render: (r) => <div style={{ height: '10px', borderRadius: '4px', backgroundColor: t.expense, opacity: 0.85, width: `${summary.peakMonth ? Math.max(2, (r.total / summary.peakMonth) * 100) : 0}%` }} />
              },
              { key: 'total', header: 'Spent', align: 'right', nowrap: true, render: (r) => <span style={{ color: t.expense, fontWeight: 'bold' }}>{formatCurrency(r.total)}</span> }
            ]}
            rows={summary.months}
            rowKey="month"
            emptyMessage="No maintenance recorded yet."
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: t.spacing.lg }}>
            <TablePanel
              title="Spend by Task"
                 isDarkMode={isDarkMode}
              columns={[
                { key: 'name', header: 'Task', render: (r) => <strong>{r.name}</strong> },
                { key: 'count', header: 'Jobs', align: 'center' },
                { key: 'total', header: 'Spent', align: 'right', nowrap: true, render: (r) => formatCurrency(r.total) }
              ]}
              rows={summary.byTask}
              rowKey="name"
              emptyMessage="No maintenance recorded yet."
              below={(() => {
                const jobs = summary.byTask.reduce((n, r) => n + (Number(r.count) || 0), 0);
                const spent = summary.byTask.reduce((n, r) => n + (Number(r.total) || 0), 0);
                return summary.byTask.length === 0 ? null : (
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', gap: '12px',
                    marginTop: '10px', paddingTop: '10px',
                    borderTop: `2px solid ${t.borderStrong}`, fontWeight: 'bold'
                  }}>
                    <span>Total &middot; {jobs} job{jobs === 1 ? '' : 's'}</span>
                    <span style={{ color: t.expense, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(spent)}</span>
                  </div>
                );
              })()}
            />
            <TablePanel
              title="Spend by Area"
                 isDarkMode={isDarkMode}
              columns={[
                { key: 'name', header: 'Area', render: (r) => <strong>{r.name}</strong> },
                { key: 'count', header: 'Jobs', align: 'center' },
                { key: 'total', header: 'Spent', align: 'right', nowrap: true, render: (r) => formatCurrency(r.total) }
              ]}
              rows={summary.byArea}
              rowKey="name"
              emptyMessage="No maintenance recorded yet."
              below={(() => {
                const jobs = summary.byArea.reduce((n, r) => n + (Number(r.count) || 0), 0);
                const spent = summary.byArea.reduce((n, r) => n + (Number(r.total) || 0), 0);
                return summary.byArea.length === 0 ? null : (
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', gap: '12px',
                    marginTop: '10px', paddingTop: '10px',
                    borderTop: `2px solid ${t.borderStrong}`, fontWeight: 'bold'
                  }}>
                    <span>Total &middot; {jobs} job{jobs === 1 ? '' : 's'}</span>
                    <span style={{ color: t.expense, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(spent)}</span>
                  </div>
                );
              })()}
            />
          </div>

          <TablePanel
            title="Reserved and Waiting (2+ years, no burial)"
               isDarkMode={isDarkMode}
            minWidth="520px"
            columns={[
              { key: 'plot', header: 'Plot', nowrap: true, render: (r) => <strong>{r.plot.section} - {r.plot.plotNumber}</strong> },
              { key: 'who', header: 'Reserved for', render: (r) => r.plot.allottedTo || '-' },
              { key: 'years', header: 'Waiting', align: 'center', render: (r) => <Badge tone={r.years >= 5 ? 'error' : 'warning'}>{r.years} yrs</Badge> },
              { key: 'since', header: 'Plot added', nowrap: true, render: (r) => formatDate(r.plot.dateAdded) },
              {
                key: 'actions', header: '', align: 'right',
                render: (r) => <Button size="sm" variant="ghost" onClick={() => bookPlot(r.plot as PlotRow)}>Open booking</Button>
              }
            ]}
            rows={summary.waiting}
            rowKey={(r) => String(r.plot.rawId)}
            emptyMessage="No long-standing reservations."
          />

          <TablePanel
            title={<>Death Anniversaries — {summary.monthName}</>}
               isDarkMode={isDarkMode}
            minWidth="520px"
            columns={[
              { key: 'day', header: 'Day', align: 'center', nowrap: true, render: (r) => <strong>{r.day}</strong> },
              { key: 'who', header: 'Name', render: (r) => <strong>{r.burial.deceasedName || '-'}</strong> },
              { key: 'years', header: 'Years', align: 'center', render: (r) => r.yearsAgo === 0 ? 'this year' : `${r.yearsAgo}` },
              { key: 'plot', header: 'Plot', nowrap: true, render: (r) => `${r.burial.section || ''} - ${r.burial.plotNumber || ''}` },
              { key: 'kin', header: 'Next of kin', render: (r) => (r.burial as BurialRow).nextOfKin || '-' }
            ]}
            rows={summary.memorials}
            rowKey={(r) => String(r.burial.rawId)}
            emptyMessage={`No death anniversaries in ${summary.monthName}. (${summary.noDeathDate} burial${summary.noDeathDate === 1 ? ' has' : 's have'} no date of death on file.)`}
          />
        </>
      )}

      {isDashboard && (
        <>
          <SubTabs
            value={dashSubTab}
            onChange={setDashSubTab}
            tabs={[
              { key: 'grid', label: 'Plot map' },
              { key: 'list', label: 'Master plot list' },
              { key: 'check', label: `Data check${mismatchCount ? ` (${mismatchCount})` : ''}`, variant: mismatchCount ? 'danger' : undefined }
            ]}
          />

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: t.spacing.md, marginBottom: t.spacing.lg
          }}>
            <KpiCard label="Blocks" value={String(blockNames.length)} color={t.info} />
            <KpiCard label="Total Plots" value={String(plots.length)} color={t.primary} />
            <KpiCard label="Available" value={String(plotCounts.Available || 0)} color={t.income} />
            <KpiCard label="Reserved" value={String(plotCounts.Reserved || 0)} color={t.warning} />
            <KpiCard label="Occupied" value={String(plotCounts.Occupied || 0)} color={t.danger} />
            <KpiCard label="Under Maintenance" value={String(plotCounts['Under Maintenance'] || 0)} color={t.textFaint} />
          </div>

          {dashSubTab === 'grid' && blockNames.length > 0 && (
            <div style={{
              display: 'flex', gap: t.spacing.md, flexWrap: 'wrap', alignItems: 'center',
              marginBottom: t.spacing.md, fontSize: t.fontSize.sm, color: t.textMuted
            }}>
              <span>Click any plot for its details.</span>
              {[
                ['Available', t.income], ['Reserved', t.warning],
                ['Occupied', t.danger], ['Under Maintenance', t.textFaint]
              ].map(([label, colour]) => (
                <span key={label as string} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{
                    width: '12px', height: '12px', borderRadius: '3px',
                    border: `2px solid ${colour}`,
                    backgroundColor: hexToRgba(colour as string, isDarkMode ? 0.22 : 0.12)
                  }} />
                  {label}
                </span>
              ))}
            </div>
          )}

          {dashSubTab === 'grid' && (
            blockNames.length === 0 ? (
              <div style={{
                padding: t.spacing.xxl, textAlign: 'center', color: t.textFaint,
                border: `1px dashed ${t.border}`, borderRadius: t.radius.xl
              }}>
                No plots yet — create some in step 1.
              </div>
            ) : (
              blockNames.map((block) => (
                <div key={block} style={{ marginBottom: t.spacing.lg }}>
                  <div style={styles.sectionTitle}>{block}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {plots.filter((p) => p.section === block).map((p) => {
                      const tone = p.status === 'Available' ? t.income
                        : p.status === 'Occupied' ? t.danger
                        : p.status === 'Reserved' ? t.warning
                        : t.textFaint;
                      return (
                        <button
                          key={p.rawId}
                          type="button"
                          onClick={() => setPlotDetail(p)}
                          title={`${p.plotNumber} — ${p.status}${p.allottedTo ? ` · ${p.allottedTo}` : ''} (click for details)`}
                          style={{
                            minWidth: '62px', padding: '8px 6px', textAlign: 'center',
                            borderRadius: t.radius.md,
                            border: `2px solid ${tone}`,
                            backgroundColor: hexToRgba(tone, isDarkMode ? 0.22 : 0.12),
                            color: t.textMain, fontSize: t.fontSize.sm, fontWeight: 'bold',
                            cursor: 'pointer', fontFamily: 'inherit',
                            transition: 'transform 0.1s ease, box-shadow 0.1s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = `0 4px 10px ${hexToRgba(tone, 0.45)}`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'none';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          {p.plotNumber}
                          <div style={{ fontSize: '10px', fontWeight: 'normal', color: t.textMuted }}>
                            {p.allottedTo || p.status}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )
          )}

          {dashSubTab === 'list' && (
            <>
              <TablePanel
                title="Cemetery Register"
                below={<Pagination {...plotPage} itemsOnPage={plotPage.currentItems.length} label="plots" />}
                columns={[...plotColumns]}
                rows={plotPage.currentItems}
                rowKey="rawId"
                loading={loading}
                emptyMessage="No plots registered yet."
              />
            </>
          )}

          {dashSubTab === 'check' && (
            mismatchCount === 0 ? (
              <div style={{
                padding: t.spacing.xxl, textAlign: 'center',
                border: `1px dashed ${t.income}`, borderRadius: t.radius.xl, color: t.income
              }}>
                <div style={{ fontSize: '28px', marginBottom: '6px' }}>✅</div>
                Every burial has its plot, every plot agrees with the burial register.
              </div>
            ) : (
              <>
                <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginBottom: t.spacing.md }}>
                  Where the plot map and the burial register disagree. Fixes in the first two
                  sections change only the plot; "Move" and "Edit burial" change the burial.
                </p>

                {mismatches.heldNotMarked.length > 0 && (
                  <div style={{ marginBottom: t.spacing.xl }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: t.spacing.md,
                      flexWrap: 'wrap', marginBottom: t.spacing.sm
                    }}>
                      <span style={{ ...styles.sectionTitle, margin: 0 }}>
                        Holds a burial, but reads Available ({mismatches.heldNotMarked.length})
                      </span>
                      <Button onClick={askFixAllHeldNotMarked} disabled={saving}>
                        Mark all Occupied
                      </Button>
                    </div>
                    <div style={{ color: t.danger, fontSize: t.fontSize.sm, marginBottom: t.spacing.sm }}>
                      These can be allotted to a second family. Worth clearing first.
                    </div>
                    <DataTable
                      columns={[
                        {
                          key: 'plot', header: 'Plot', nowrap: true,
                          render: (r) => <strong>{r.plot.section} - {r.plot.plotNumber}</strong>
                        },
                        { key: 'reads', header: 'Plot reads', render: (r) => <Badge tone="success">{r.plot.status}</Badge> },
                        { key: 'who', header: 'Burial on file', render: (r) => r.burial.deceasedName || '-' },
                        { key: 'when', header: 'Buried', nowrap: true, render: (r) => formatDate(r.burial.date) },
                        { key: 'ref', header: 'Burial record', render: (r) => r.burial.recordId || '-' },
                        {
                          key: 'fix', header: 'Fix', align: 'right', nowrap: true,
                          render: (r) => (
                            <Button size="sm" disabled={saving} onClick={() => askFixHeldNotMarked(r)}>
                              Mark Occupied
                            </Button>
                          )
                        }
                      ]}
                      rows={mismatches.heldNotMarked}
                      rowKey={(r) => String(r.plot.rawId)}
                      emptyMessage=""
                    />
                  </div>
                )}

                {orphans.length > 0 && (
                  <div style={{ marginBottom: t.spacing.xl }}>
                    <div style={styles.sectionTitle}>
                      Burial names a plot that does not exist ({orphans.length})
                    </div>
                    <div style={{ color: t.danger, fontSize: t.fontSize.sm, marginBottom: t.spacing.sm }}>
                      These graves are not on the map. Usually the block was typed wrong — where a plot
                      with the same number is free in another block, "Move" puts the burial there.
                      Otherwise "Create plot" cuts the plot the record names, already Occupied.
                    </div>
                    <DataTable
                      columns={[
                        { key: 'who', header: 'Buried', render: (r) => <><strong>{r.burial.deceasedName || '-'}</strong><br /><span style={{ fontSize: '11px', color: t.textFaint }}>{r.burial.recordId} · {formatDate(r.burial.date)}</span></> },
                        { key: 'named', header: 'Plot named', nowrap: true, render: (r) => <Badge tone="error">{r.burial.section || '?'} - {r.burial.plotNumber || '?'}</Badge> },
                        {
                          key: 'fix', header: 'Fix', align: 'right',
                          render: (r) => (
                            <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                              {r.candidates.length > 0 && (
                                <Select placeholder="Move to…" containerStyle={{ margin: 0, minWidth: '170px' }} value=""
                                  options={r.candidates.map((c) => ({ value: String(c.rawId), label: `${c.section} - ${c.plotNumber} (${c.status})` }))}
                                  onChange={(e) => { const target = r.candidates.find((c) => String(c.rawId) === e.target.value); if (target) repointBurial(r.burial, target as PlotRow); }} />
                              )}
                              <Button size="sm" variant="secondary" disabled={saving || !r.burial.section || !r.burial.plotNumber}
                                onClick={() => createPlotForBurial(r.burial)}>Create plot</Button>
                              <Button size="sm" variant="ghost" onClick={() => { switchTab('burials'); startEdit(r.burial); }}>Edit burial</Button>
                            </div>
                          )
                        }
                      ]}
                      rows={orphans}
                      rowKey={(r) => String(r.burial.rawId)}
                      emptyMessage=""
                    />
                  </div>
                )}

                {doubles.length > 0 && (
                  <div style={{ marginBottom: t.spacing.xl }}>
                    <div style={styles.sectionTitle}>
                      Two burials in one plot ({doubles.length})
                    </div>
                    <div style={{ color: t.danger, fontSize: t.fontSize.sm, marginBottom: t.spacing.sm }}>
                      One of each pair is on the wrong plot, or is a duplicate entry. Open each and correct or delete it.
                    </div>
                    <DataTable
                      columns={[
                        { key: 'plot', header: 'Plot', nowrap: true, render: (r) => <strong>{r.section} - {r.plotNumber}</strong> },
                        {
                          key: 'who', header: 'Burials on file', wrap: true,
                          render: (r) => r.burials.map((b) => (
                            <div key={b.recordId} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '2px 0' }}>
                              <span>{b.deceasedName || '-'} <span style={{ fontSize: '11px', color: t.textFaint }}>{b.recordId} · {formatDate(b.date)}</span></span>
                              <Button size="sm" variant="ghost" onClick={() => { switchTab('burials'); startEdit(b as BurialRow); }}>Edit</Button>
                            </div>
                          ))
                        }
                      ]}
                      rows={doubles}
                      rowKey={(r) => `${r.section}-${r.plotNumber}`}
                      emptyMessage=""
                    />
                  </div>
                )}

                {mismatches.markedNotHeld.length > 0 && (
                  <div>
                    <div style={styles.sectionTitle}>
                      Reads Occupied, but no burial names it ({mismatches.markedNotHeld.length})
                    </div>
                    <div style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginBottom: t.spacing.sm }}>
                      A grave held back for no recorded reason. Check on the ground before
                      releasing — the burial may simply never have been entered.
                    </div>
                    <DataTable
                      columns={[
                        {
                          key: 'plot', header: 'Plot', nowrap: true,
                          render: (r) => <strong>{r.section} - {r.plotNumber}</strong>
                        },
                        { key: 'reads', header: 'Plot reads', render: () => <Badge tone="error">Occupied</Badge> },
                        { key: 'allottedTo', header: 'Allotted to', render: (r) => r.allottedTo || '-' },
                        { key: 'added', header: 'Added', nowrap: true, render: (r) => formatDate(r.dateAdded) },
                        { key: 'ref', header: 'Plot record', render: (r) => r.recordId || '-' },
                        {
                          key: 'fix', header: 'Fix', align: 'right', nowrap: true,
                          render: (r) => (
                            <Button variant="secondary" size="sm" disabled={saving}
                              onClick={() => fixMarkedNotHeld(r)}>
                              Release
                            </Button>
                          )
                        }
                      ]}
                      rows={mismatches.markedNotHeld}
                      rowKey="rawId"
                      emptyMessage=""
                    />
                  </div>
                )}
              </>
            )
          )}
        </>
      )}

      {!isDashboard && !isSummary && (viewMode === 'history' || isDeletedView) && (
        <>
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder="Search by Name, Allotted To, Plot No, Area, Task or ID..."
            actions={(
              <>
                <PrintButton
                  module="Cemetery"
                  documentType={(activeTab === 'burials' ? { doc: 'CEMETERY_BURIAL_REGISTER', title: 'Burial Register' } : activeTab === 'maintenance' ? { doc: 'CEMETERY_MAINTENANCE_REGISTER', title: 'Cemetery Maintenance Log' } : { doc: 'CEMETERY_REGISTER', title: 'Plot Register' }).doc}
                  size="sm"
                  title={(activeTab === 'burials' ? { doc: 'CEMETERY_BURIAL_REGISTER', title: 'Burial Register' } : activeTab === 'maintenance' ? { doc: 'CEMETERY_MAINTENANCE_REGISTER', title: 'Cemetery Maintenance Log' } : { doc: 'CEMETERY_REGISTER', title: 'Plot Register' }).title.replace(/[^A-Za-z0-9]+/g, '-')}
                  // Each tab prints its own register, with every row that matches the
                  // filters — dates formatted and Created By on every line.
                  data={() => ({
                    title: `${(activeTab === 'burials' ? { doc: 'CEMETERY_BURIAL_REGISTER', title: 'Burial Register' } : activeTab === 'maintenance' ? { doc: 'CEMETERY_MAINTENANCE_REGISTER', title: 'Cemetery Maintenance Log' } : { doc: 'CEMETERY_REGISTER', title: 'Plot Register' }).title}${isDeletedView ? ' (Deleted)' : ''}`,
                    reference: printReference(searchTerm && `Search: "${searchTerm}"`),
                    rowCount: filtered.length,
                    rows: printableRows(filtered)
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
                <DateRangeFilter {...range} onChange={page.reset} />
              </>
            )}
          />

          <TablePanel
            title={isSetup ? 'Plots' : isAllotment ? 'Burials' : 'Maintenance'}
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="records" />}
            columns={columns}
            rows={page.currentItems}
            rowKey="rawId"
            loading={loading}
            emptyMessage="No records found for this category."
          />
        </>
      )}

      {viewMode === 'add' && isAllotment && (
        <FormLayout
          title="Book / Allot a Plot"
          submitLabel="Confirm Booking"
          submitting={saving}
          onSubmit={handleAllotmentSubmit}
        >
          <FormLayout.Grid>
            <SearchableSelect
              label="Plot to Book" required
              placeholder="Search an available plot..."
              value={allotForm.targetPlot}
              onChange={(v) => setAllotForm((prev) => ({ ...prev, targetPlot: v }))}
              options={openPlotLabels}
              allowAdd={false}
              allowManage={false}
            />
            <Input
              label="Allotted To" required placeholder="Name of the person or family"
              value={allotForm.allottedTo}
              onChange={(e) => setAllotForm({ ...allotForm, allottedTo: e.target.value })}
            />
            <Select
              label="Booking Status" placeholder=""
              options={['Reserved', 'Pre-Reserved']}
              value={allotForm.bookingStatus}
              onChange={(e) => setAllotForm({ ...allotForm, bookingStatus: e.target.value })}
            />
            <Input
              label="Notes" type="textarea" rows={2} fullWidth
              placeholder="Booking reference, payment received, family wishes..."
              value={allotForm.notes}
              onChange={(e) => setAllotForm({ ...allotForm, notes: e.target.value })}
            />
          </FormLayout.Grid>

          {openPlotLabels.length === 0 && (
            <div style={{ color: t.warning, fontWeight: 'bold', marginTop: t.spacing.sm }}>
              No available plots left to book — create more in step 1.
            </div>
          )}
        </FormLayout>
      )}

      {viewMode === 'add' && isSetup && (
        <>
          <SubTabs
            value={setupMode}
            onChange={setSetupMode}
            tabs={[
              { key: 'single', label: 'Single plot' },
              { key: 'bulk', label: 'Bulk generator' }
            ]}
          />

          {setupMode === 'bulk' && (
            <FormLayout
              title="Bulk Generate Plots"
              submitLabel="Generate Plots"
              submitting={saving}
              onSubmit={handleBulkSubmit}
            >
              <FormLayout.Grid>
                <SearchableSelect
                  label="Section / Block" required
                  placeholder="Search or type section..."
                  value={bulkForm.section}
                  onChange={(v) => setBulkForm((prev) => ({ ...prev, section: v }))}
                  options={sections.options}
                  onOptionsChange={sections.setOptions}
                />
                <Input
                  label="Plot Prefix (optional)" placeholder="e.g. P-"
                  value={bulkForm.prefix}
                  onChange={(e) => setBulkForm({ ...bulkForm, prefix: e.target.value })}
                />
                <Input
                  label="Start Number" type="number" min="1" required
                  value={bulkForm.startNum}
                  onChange={(e) => setBulkForm({ ...bulkForm, startNum: e.target.value })}
                />
                <Input
                  label="End Number" type="number" min="1" required
                  value={bulkForm.endNum}
                  onChange={(e) => setBulkForm({ ...bulkForm, endNum: e.target.value })}
                />
              </FormLayout.Grid>

              <div style={{ marginTop: t.spacing.sm, color: t.textMuted, fontSize: t.fontSize.md }}>
                Creates{' '}
                <strong>
                  {bulkForm.prefix}{bulkForm.startNum} … {bulkForm.prefix}{bulkForm.endNum}
                </strong>{' '}
                in {bulkForm.section || 'the chosen section'}. Numbers that already exist are skipped.
              </div>
            </FormLayout>
          )}
        </>
      )}

      {viewMode === 'add' && isSetup && setupMode === 'single' && (
        <FormLayout
          title={editing ? `Edit Plot ${editing.recordId || ''}` : 'Register New Grave Plot'}
          submitLabel={editing ? 'Update Plot' : 'Save Plot Details'}
          submitting={saving}
          onCancel={editing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
          onSubmit={handlePlotSubmit}
        >
          <FormLayout.Grid>
            <Input
              label="Plot Number / Identifier" required placeholder="e.g. 101, 102, 103"
              value={plotForm.plotNumber}
              onChange={(e) => setPlotForm({ ...plotForm, plotNumber: e.target.value })}
            />
            <SearchableSelect
              label="Section / Block"
              required
              placeholder="Search or type section..."
              value={plotForm.section}
              onChange={(v) => setPlotForm((prev) => ({ ...prev, section: v }))}
              options={sections.options}
              onOptionsChange={sections.setOptions}
            />
            <Select
              label="Current Status" placeholder=""
              options={PLOT_STATUSES}
              value={plotForm.status}
              onChange={(e) => setPlotForm({ ...plotForm, status: e.target.value })}
            />
            <Input
              label="Allotted To / Reserved For" placeholder="e.g. John Doe Family (Optional)"
              value={plotForm.allottedTo}
              onChange={(e) => setPlotForm({ ...plotForm, allottedTo: e.target.value })}
            />
            <Input
              label="Plot Notes & Dimensions" type="textarea" rows={2} fullWidth
              placeholder="e.g. Standard single plot, near the north gate..."
              value={plotForm.notes}
              onChange={(e) => setPlotForm({ ...plotForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {viewMode === 'add' && isBurials && (
        <FormLayout
          title={editing ? `Edit Burial ${editing.recordId || ''}` : 'Record New Burial / Interment'}
          submitLabel={editing ? 'Update Burial Record' : 'Save Burial Record'}
          submitting={saving}
          onCancel={editing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
          onSubmit={handleBurialSubmit}
        >
          <FormLayout.Grid>
            <Input
              label="Burial Date" type="date" required
              value={burialForm.date}
              onChange={(e) => setBurialForm({ ...burialForm, date: e.target.value })}
            />
            <Input
              label="Date of Death"
              type="date"
              value={burialForm.dateOfDeath}
              onChange={(e) => setBurialForm({ ...burialForm, dateOfDeath: e.target.value })}
            />
            {/* Optional throughout: the churchyard also receives people who were
                never on these rolls. Picking a member is what marks them
                deceased in the directory, so nobody types the date twice. */}
            <MemberPicker
              label="Church Member"
              hint="Leave blank if the person was not on the church rolls."
              fullWidth
              isDarkMode={isDarkMode}
              value={burialForm.memberId}
              onChange={(memberId, member) => setBurialForm((prev) => ({
                ...prev,
                memberId,
                deceasedName: member?.name || prev.deceasedName,
                dateOfDeath: member?.dateOfDeath || prev.dateOfDeath
              }))}
            />
            <SearchableSelect
              label="Name of Deceased"
              required
              allowAdd={false}
              allowManage={false}
              placeholder="Search reservations or type new..."
              value={burialForm.deceasedName}
              onChange={selectDeceased}
              options={reservedNames}
            />
            <SearchableSelect
              label="Assigned Plot (Available/Reserved Only)"
              required
              allowAdd={false}
              allowManage={false}
              placeholder="Search available plots..."
              value={burialForm.assignedPlot}
              onChange={selectPlot}
              options={burialPlotOptions}
            />
            <Input
              label="Next of Kin / Contact" required placeholder="Name & Phone Number"
              value={burialForm.nextOfKin}
              onChange={(e) => setBurialForm({ ...burialForm, nextOfKin: e.target.value })}
            />
            <Input
              label="Additional Remarks" type="textarea" rows={2} fullWidth
              placeholder="Service details, headstone preferences..."
              value={burialForm.notes}
              onChange={(e) => setBurialForm({ ...burialForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {viewMode === 'add' && isMaintenance && (
        <FormLayout
          title={editing ? `Edit Maintenance ${editing.recordId || ''}` : 'Log Grounds Maintenance'}
          submitLabel={editing ? 'Update Log' : 'Save Maintenance Log'}
          submitting={saving}
          onCancel={editing ? () => switchView('history') : undefined}
          cancelLabel="Cancel Edit"
          onSubmit={handleMaintenanceSubmit}
        >
          <FormLayout.Grid>
            <Input
              label="Date of Work" type="date" required max={todayISO()}
              value={maintForm.date}
              onChange={(e) => setMaintForm({ ...maintForm, date: e.target.value })}
            />
            <SearchableSelect
              label="Area / Section" required
              placeholder="Search or type area..."
              value={maintForm.area}
              onChange={(v) => setMaintForm((prev) => ({ ...prev, area: v }))}
              options={sections.options}
              onOptionsChange={sections.setOptions}
            />
            <SearchableSelect
              label="Task Type" required
              placeholder="Search or type task..."
              value={maintForm.taskType}
              onChange={(v) => setMaintForm((prev) => ({ ...prev, taskType: v }))}
              options={taskTypes.options}
              onOptionsChange={taskTypes.setOptions}
            />
            <Input
              label="Cost (₹)" type="number" step="0.01" placeholder="0.00"
              value={maintForm.cost}
              onChange={(e) => setMaintForm({ ...maintForm, cost: e.target.value })}
              style={{ color: t.expense, fontWeight: 'bold' }}
            />
            <Input
              label="Notes / Work Done" type="textarea" rows={2} fullWidth
              placeholder="Contractor, materials used, follow-up needed..."
              value={maintForm.notes}
              onChange={(e) => setMaintForm({ ...maintForm, notes: e.target.value })}
            />
          </FormLayout.Grid>
        </FormLayout>
      )}

      {plotDetail && (() => {
        const p = plotDetail;
        const filled = burialFor(p);
        const free = p.status === 'Available' || p.status === 'Reserved';
        const row = (label: string, value) => (
          <div style={{ display: 'flex', gap: '10px', padding: '7px 0', borderBottom: `1px solid ${t.borderDashed}` }}>
            <span style={{ minWidth: '130px', color: t.textMuted, fontSize: t.fontSize.sm }}>{label}</span>
            <span style={{ color: t.textMain, fontSize: t.fontSize.md }}>{value || '-'}</span>
          </div>
        );

        return (
          <Modal
            open
            isDarkMode={isDarkMode}
            width="520px"
            title={`${p.section} - ${p.plotNumber}`}
            onClose={() => setPlotDetail(null)}
            footer={
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button variant="secondary" onClick={() => setPlotDetail(null)}>Close</Button>
                {free && (
                  <Button variant="ghost" onClick={() => bookPlot(p)}>
                    {p.status === 'Reserved' ? 'Re-book' : 'Book / Allot'}
                  </Button>
                )}
                {free && <Button onClick={() => buryInPlot(p)}>Record Burial Here</Button>}
                {filled && <Button onClick={openBurialRegistry}>Open Burial Registry</Button>}
              </div>
            }
          >
            <div style={{ marginBottom: t.spacing.md }}>
              <Badge tone={plotStatusTone(p.status)}>{p.status}</Badge>
            </div>

            {row('Plot', `${p.section} - ${p.plotNumber}`)}
            {row('Plot record', p.recordId)}
            {row('Allotted to', p.allottedTo)}
            {row('Added on', p.dateAdded ? formatDate(p.dateAdded) : '')}
            {row('Notes', p.notes)}

            {filled ? (
              <>
                <div style={{ ...styles.sectionTitle, marginTop: t.spacing.lg }}>Burial in this plot</div>
                {row('Deceased', filled.deceasedName)}
                {row('Date of death', filled.dateOfDeath ? formatDate(filled.dateOfDeath) : '')}
                {row('Buried on', filled.date ? formatDate(filled.date) : '')}
                {row('Church member', filled.memberId)}
                {row('Next of kin', filled.nextOfKin)}
                {row('Burial record', filled.recordId)}
                {filled.notes ? row('Remarks', filled.notes) : null}
              </>
            ) : (
              <div style={{ marginTop: t.spacing.lg, color: t.textFaint, fontSize: t.fontSize.sm }}>
                {p.status === 'Occupied'
                  ? 'Marked occupied, but no burial record names this plot. Worth checking.'
                  : 'No burial recorded in this plot.'}
              </div>
            )}
          </Modal>
        );
      })()}

      <ConfirmDialog
        open={Boolean(pendingOccupy)}
        isDarkMode={isDarkMode}
        title="Mark this plot Occupied?"
        message={pendingOccupy?.message}
        confirmLabel={pendingOccupy?.confirmLabel || 'Confirm'}
        cancelLabel="Go back"
        destructive={false}
        onConfirm={() => { const job = pendingOccupy; setPendingOccupy(null); job?.run(); }}
        onCancel={() => setPendingOccupy(null)}
      />

      <PromptDialog
        open={Boolean(voiding)}
        title={`Delete ${voiding?.recordType || ''} ${voiding?.recordId || ''}`}
        message="Please enter the reason for removing this record. It will be kept in Deleted History."
        placeholder="Reason for voiding"
        confirmLabel="Delete"
        requiredError="A reason is required to remove a record."
        onSubmit={confirmVoid}
        onCancel={() => setVoiding(null)}
      />
    </div>
  );
};

export default CemeteryManagement;
